/**
 * MediaUploadModal Component
 *
 * Modal for uploading media with drag-and-drop and file browsing.
 * Features:
 * - Drag & drop zone
 * - Click to browse (native file picker)
 * - File thumbnail grid with hover actions
 * - Per-file crop button
 * - Validation (file type, size, count)
 * - Cancel and Upload actions
 */

import { ArrowUpToLine, Crop, FileIcon, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib"
import { ImageCropperModal } from "./ImageCropperModal"
import { ProfileImageCropperModal } from "./ProfileImageCropperModal"

interface SelectedFile {
  id: string
  file: File
  preview: string
  isImage: boolean
  hasError: boolean
  errorMessage?: string
  croppedFile?: File
}

interface MediaUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (files: File[]) => Promise<void>
  maxFiles?: number
  maxFileSize?: number
  allowedTypes?: string[]
  title?: string
  isUploading?: boolean
  existingFilesCount?: number
  cropperType?: "general" | "profile"
  initialFile?: File // Pre-selected file to open directly in cropper
}

const DEFAULT_MAX_FILES = 10
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024
const DEFAULT_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"]

export function MediaUploadModal({
  open,
  onOpenChange,
  onUpload,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  title = "Upload Media",
  isUploading = false,
  existingFilesCount = 0,
  cropperType = "general",
  initialFile,
}: MediaUploadModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [isDragActive, setIsDragActive] = useState(false)
  const [cropperFile, setCropperFile] = useState<SelectedFile | null>(null)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  useEffect(() => {
    if (!open) {
      setSelectedFiles([])
      setIsDragActive(false)
      setCropperFile(null)
      setCropperOpen(false)
      dragCounterRef.current = 0
    } else if (open && initialFile) {
      // When opening with initialFile, go directly to cropper
      const isImage = initialFile.type.startsWith("image/")
      const selectedFile: SelectedFile = {
        id: `initial-${Date.now()}`,
        file: initialFile,
        preview: isImage ? URL.createObjectURL(initialFile) : "",
        isImage,
        hasError: false,
      }
      setCropperFile(selectedFile)
      setCropperOpen(true)
      setSelectedFiles([selectedFile])
    }
  }, [open, initialFile])

  const validateFile = useCallback(
    (file: File): { valid: boolean; message?: string } => {
      if (!allowedTypes.includes(file.type)) {
        const allowedExts = allowedTypes.map(t => t.split("/")[1].toUpperCase()).join(", ")
        return { valid: false, message: `Invalid type. Allowed: ${allowedExts}` }
      }

      if (file.size > maxFileSize) {
        const maxSizeMB = maxFileSize / (1024 * 1024)
        return { valid: false, message: `Exceeds ${maxSizeMB}MB limit` }
      }

      return { valid: true }
    },
    [allowedTypes, maxFileSize],
  )

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const availableSlots = maxFiles - selectedFiles.length - existingFilesCount

      if (availableSlots <= 0) {
        toast.error(`Maximum ${maxFiles} files already uploaded`)
        return
      }

      if (fileArray.length > availableSlots) {
        toast.error(
          `Only ${availableSlots} more file${availableSlots !== 1 ? "s" : ""} can be added`,
        )
        fileArray.splice(availableSlots)
      }

      const newFiles: SelectedFile[] = fileArray.map(file => {
        const validation = validateFile(file)
        const isImage = file.type.startsWith("image/")

        return {
          id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          file,
          preview: isImage ? URL.createObjectURL(file) : "",
          isImage,
          hasError: !validation.valid,
          errorMessage: validation.message,
          croppedFile: undefined,
        }
      })

      setSelectedFiles(prev => [...prev, ...newFiles])

      if (maxFiles === 1 && newFiles.length > 0 && newFiles[0].isImage) {
        setCropperFile(newFiles[0])
        setCropperOpen(true)
      }
    },
    [maxFiles, selectedFiles.length, existingFilesCount, validateFile],
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragActive(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragActive(false)
      dragCounterRef.current = 0

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleRemoveFile = useCallback((id: string) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === id)
      if (file?.preview) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter(f => f.id !== id)
    })
  }, [])

  const handleCropClick = useCallback((file: SelectedFile) => {
    setCropperFile(file)
    setCropperOpen(true)
  }, [])

  const handleCropComplete = useCallback(
    async (croppedFile: File) => {
      if (!cropperFile) return

      if (cropperFile.preview) {
        URL.revokeObjectURL(cropperFile.preview)
      }

      // If initialFile was provided, upload directly without showing the modal
      if (initialFile) {
        setIsProcessing(true)
        try {
          await onUpload([croppedFile])
          setCropperOpen(false) // Close cropper first
          onOpenChange(false) // Then close the modal
        } catch (error) {
          console.error("Upload failed:", error)
        } finally {
          setIsProcessing(false)
        }
        return
      }

      // Normal flow: update selectedFiles and return to modal
      const newPreview = URL.createObjectURL(croppedFile)

      setSelectedFiles(prev =>
        prev.map(f =>
          f.id === cropperFile.id
            ? { ...f, file: croppedFile, croppedFile, preview: newPreview }
            : f,
        ),
      )
      setCropperFile(null)
    },
    [cropperFile, initialFile, onUpload, onOpenChange],
  )

  const handleUpload = useCallback(async () => {
    const validFiles = selectedFiles.filter(f => !f.hasError)

    if (validFiles.length === 0) {
      toast.error("No valid files to upload")
      return
    }

    setIsProcessing(true)
    try {
      const filesToUpload = validFiles.map(f => f.croppedFile || f.file)
      await onUpload(filesToUpload)
      selectedFiles.forEach(f => {
        if (f.preview) {
          URL.revokeObjectURL(f.preview)
        }
      })
      setSelectedFiles([])
      onOpenChange(false)
    } catch (error) {
      console.error("Upload failed:", error)
      toast.error("Upload failed. Please try again.")
    } finally {
      setIsProcessing(false)
    }
  }, [selectedFiles, onUpload, onOpenChange])

  if (!open) return null

  const hasFiles = selectedFiles.length > 0
  const hasErrors = selectedFiles.some(f => f.hasError)

  return (
    <>
      {!cropperOpen && !initialFile && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedTypes.join(",")}
            multiple
            onChange={e => {
              if (e.target.files) {
                addFiles(e.target.files)
                e.target.value = ""
              }
            }}
            className="hidden"
          />

          <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-auto" preventCloseOnOutsideClick>
              <DialogTitle className="text-[20px] font-semibold leading-none mt-1 leading-[30px]">
                {title}
              </DialogTitle>

              <div className="overflow-auto">
                {selectedFiles.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-[11px] font-medium">Attached files</p>
                      <p className="text-[11px] text-muted-foreground">
                        {selectedFiles.length + existingFilesCount}/{maxFiles} uploaded
                      </p>
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
                      {selectedFiles.map(file => (
                        <div
                          key={file.id}
                          className={cn(
                            "group relative rounded-xl overflow-hidden",
                            file.hasError ? "border-red" : "border-border",
                          )}
                        >
                          <div className="aspect-square bg-muted">
                            {file.isImage && file.preview ? (
                              <img
                                src={file.preview}
                                alt={file.file.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <FileIcon className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {file.hasError && (
                            <>
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-red/30 p-2">
                                <p className="text-xs text-white font-[500] leading-[20px] text-center leading-tight">
                                  {file.errorMessage}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleRemoveFile(file.id)
                                }}
                                className="absolute top-2 right-2 z-10 rounded-full bg-red/80 p-1.5 hover:bg-red transition-colors cursor-pointer"
                                title="Remove"
                              >
                                <X className="h-3.5 w-3.5 text-white" />
                              </button>
                            </>
                          )}

                          {!file.hasError && (
                            <div className="absolute inset-0 hidden group-hover:flex items-center justify-center gap-2 bg-[rgba(0,0,0,0.6)] transition-all">
                              {file.isImage && (
                                <button
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleCropClick(file)
                                  }}
                                  className="rounded-full bg-white/20 p-2 transition-colors cursor-pointer"
                                  title="Crop image"
                                >
                                  <Crop className="h-4 w-4 text-white" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleRemoveFile(file.id)
                                }}
                                className="rounded-full p-2 bg-red/60 transition-colors cursor-pointer"
                                title="Remove"
                              >
                                <X className="h-4 w-4 text-white" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 rounded-xl cursor-pointer transition-all duration-200 py-6 px-6 bg-water-light border-1 border-water-light border-dashed",
                    isDragActive ? "border-primary bg-primary/5" : "",
                    selectedFiles.length > 0 ? "mt-6 mb-4" : "mt-0",
                  )}
                >
                  <div className="bg-water p-2.5 rounded-full">
                    <ArrowUpToLine
                      className={cn("h-5 w-5", isDragActive ? "text-primary" : "text-primary")}
                    />
                  </div>
                  <p
                    className={cn(
                      "text-primary font-medium my-0 text-[11px] leading-[25px] font-semibold",
                    )}
                  >
                    {isDragActive ? "Drop files here" : "Drag & Drop your media here"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">or</p>
                  <Button variant="outline" size="sm" className="mt-1 mb-4 border-white">
                    Browse Files
                  </Button>
                  <p className="text-[10px] text-body font-semibold">
                    Up to {maxFiles} files.{" "}
                    {allowedTypes.map(t => t.split("/")[1].toUpperCase()).join(", ")} up to{" "}
                    {(maxFileSize / 1024 / 1024).toFixed(0)}MB ·
                  </p>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <DialogFooter className="">
                  <Button
                    onClick={handleUpload}
                    className="px-18 py-0 h-[40px] uppercase text-[11px]"
                    disabled={!hasFiles || hasErrors || isProcessing || isUploading}
                    isLoading={isProcessing || isUploading}
                  >
                    Save
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {cropperOpen && cropperFile && cropperType === "profile" ? (
        <ProfileImageCropperModal
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageFile={cropperFile.croppedFile || cropperFile.file}
          onCropComplete={handleCropComplete}
        />
      ) : cropperOpen && cropperFile ? (
        <ImageCropperModal
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageFile={cropperFile.croppedFile || cropperFile.file}
          onCropComplete={handleCropComplete}
        />
      ) : null}
    </>
  )
}
